import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../auth/auth.service';
import { roleNamesOf } from '../../../auth/menu-access';
import { OrderProfile, PurchaseListLine, PurchaseListView, PurchaseSettings, PurchasingService, roundToPack, VAT_RATE } from '../../api';

const DAY_LABELS = ['', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

/**
 * Beszerzés → Rendelési lista: Emese tételenként jelöli „Van elég" / „Rendelni kell" (darabszámmal,
 * a szokásos mennyiséggel előtöltve), majd leadja — beszállítónként rendelés lesz belőle, és Foxi intézi.
 */
@Component({
  selector: 'app-rendelesi-lista',
  templateUrl: './rendelesi-lista.component.html',
  styleUrl: './rendelesi-lista.component.scss',
})
export class RendelesiListaComponent implements OnInit, OnDestroy {
  loading = true;
  loadFailed = false;
  data: PurchaseListView | null = null;
  submitting = false;
  submitError = '';
  submitted: { displayId: string; supplier: string; statusLabel: string }[] | null = null;

  settings: PurchaseSettings | null = null;
  settingsOpen = false;
  settingsForm: { days: number[]; remindTimes: string; responsible: number | null; approver: number | null; limit: number; deliveryNote: string } | null = null;
  savingSettings = false;
  /** rendelési adatok (vendég-vásárlás a webshopokban, rendelő levelek) + webshop-robot */
  profile: OrderProfile | null = null;
  profileForm: Partial<OrderProfile> | null = null;
  readonly dayLabels = DAY_LABELS;
  readonly weekdays = [1, 2, 3, 4, 5, 6, 7];

  private readonly qty$ = new Subject<PurchaseListLine>();
  private readonly subs: Subscription[] = [];

  constructor(private purchasing: PurchasingService, private notification: NotificationService, private auth: AuthService) {}

  ngOnInit(): void {
    this.load();
    this.purchasing.getSettings().subscribe({ next: (s) => (this.settings = s), error: () => undefined });
    this.purchasing.getProfile().subscribe({ next: (p) => (this.profile = p), error: () => undefined });
    this.subs.push(this.qty$.pipe(debounceTime(400)).subscribe((line) => this.save(line)));
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get isAdmin(): boolean {
    return roleNamesOf(this.auth.user).includes('admin');
  }

  load(): void {
    this.loading = !this.data;
    this.purchasing.getList().subscribe({
      next: (data) => {
        this.data = data;
        this.loading = false;
        this.loadFailed = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  get checkDaysLabel(): string {
    const days = `${this.data?.checkDays || ''}`.split(',').map(Number).filter(Boolean);
    return days.map((d) => DAY_LABELS[d]).join(', ') || '—';
  }

  get unmarkedCount(): number {
    return (this.data?.suppliers || []).reduce((n, s) => n + s.lines.filter((l) => !l.decision).length, 0);
  }

  get totalLines(): number {
    return (this.data?.suppliers || []).reduce((n, s) => n + s.lines.length, 0);
  }

  /** a jelölés visszavonása (a tétel újra „nincs megjelölve") */
  clearLine(line: PurchaseListLine): void {
    line.decision = null;
    line.quantity = null;
    this.save(line);
  }

  /** a teljes lista újrakezdése */
  clearAll(): void {
    if (!confirm('Biztosan törlöd a lista összes jelölését és darabszámát?')) return;
    this.purchasing.clearList().subscribe({
      next: () => {
        this.notification.show('A lista kiürítve.', 'success');
        this.submitted = null;
        this.load();
      },
      error: (e) => this.notification.show(e?.error?.message || 'Nem sikerült.', 'danger'),
    });
  }

  get markedCount(): number {
    return this.totalLines - this.unmarkedCount;
  }

  setDecision(line: PurchaseListLine, decision: 'ok' | 'order'): void {
    line.decision = decision;
    if (decision === 'order' && !line.quantity) line.quantity = line.usualQty || null;
    if (decision === 'ok') line.quantity = null;
    this.submitError = '';
    this.save(line);
  }

  onQty(line: PurchaseListLine, value: string | number): void {
    const n = Math.round(Number(value));
    line.quantity = Number.isFinite(n) && n > 0 ? n : null;
    this.qty$.next(line);
  }

  private save(line: PurchaseListLine): void {
    this.purchasing.setLine(line.itemId, line.decision, line.quantity).subscribe({
      error: (e) => this.notification.show(e?.error?.message || 'Nem sikerült menteni.', 'danger'),
    });
  }

  rounded(line: PurchaseListLine): number {
    return roundToPack(Number(line.quantity) || 0, line.packSize);
  }

  /** Beszállítónkénti összesítés a „rendelni kell" tételekből. */
  get summary(): { name: string; orderMethod: string; items: string; gross: number; priced: boolean; auto: boolean }[] {
    const limit = this.data?.autoOrderLimitHuf || 0;
    return (this.data?.suppliers || [])
      .map((s) => {
        const lines = s.lines.filter((l) => l.decision === 'order' && Number(l.quantity) > 0);
        if (!lines.length) return null;
        const priced = lines.every((l) => l.unitPrice !== null);
        const sum = lines.reduce((total, l) => total + this.rounded(l) * (Number(l.unitPrice) || 0), 0);
        // vevői webshopnál az ár már bruttó
        const gross = Math.round((s.pricesGross ? sum : sum * (1 + VAT_RATE)) + (Number(s.shippingFee) || 0));
        return {
          name: s.name,
          orderMethod: s.orderMethod,
          items: lines.map((l) => `${this.rounded(l)} db ${l.name}`).join(', '),
          gross,
          priced,
          auto: !!limit && priced && gross <= limit,
        };
      })
      .filter((x): x is NonNullable<typeof x> => !!x);
  }

  submit(): void {
    if (this.unmarkedCount) {
      this.submitError = `Még ${this.unmarkedCount} tétel nincs megjelölve.`;
      return;
    }
    const missing = (this.data?.suppliers || []).flatMap((s) => s.lines).filter((l) => l.decision === 'order' && !(Number(l.quantity) > 0));
    if (missing.length) {
      this.submitError = `Adj meg darabszámot: ${missing.map((l) => l.name).join(', ')}.`;
      return;
    }
    this.submitting = true;
    this.submitError = '';
    this.purchasing.submitList().subscribe({
      next: (result) => {
        this.submitting = false;
        this.submitted = result.orders.map((o) => ({ displayId: o.displayId, supplier: o.supplier?.name || '', statusLabel: o.statusLabel }));
        this.notification.show(result.orders.length ? 'Leadva — Foxi intézi a rendeléseket. 🦊' : 'Leadva — most nem kell semmit rendelni.', 'success');
        this.load();
      },
      error: (e) => {
        this.submitting = false;
        this.submitError = e?.error?.message || 'Nem sikerült leadni.';
      },
    });
  }

  // ------------------------------------------------------------ beállítások (admin)

  openSettings(): void {
    const s = this.settings;
    if (!s) return;
    this.settingsForm = {
      days: `${s.checkDays || ''}`.split(',').map(Number).filter(Boolean),
      remindTimes: s.remindTimes,
      responsible: s.responsibleUserId,
      approver: s.escalateUserId,
      limit: s.autoOrderLimitHuf,
      deliveryNote: s.deliveryNote,
    };
    const p = this.profile;
    this.profileForm = p
      ? { company_name: p.company_name, tax_number: p.tax_number, contact_name: p.contact_name, email: p.email, phone: p.phone, country: p.country, zip: p.zip, city: p.city, street: p.street, note: p.note,
          ship_same: p.ship_same, ship_name: p.ship_name, ship_phone: p.ship_phone, ship_country: p.ship_country, ship_zip: p.ship_zip, ship_city: p.ship_city, ship_street: p.ship_street,
          robot_dry_run: p.robot_dry_run, robot_enabled: p.robot_enabled }
      : null;
    this.settingsOpen = true;
  }

  toggleDay(day: number): void {
    if (!this.settingsForm) return;
    const days = this.settingsForm.days;
    this.settingsForm.days = days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort();
  }

  saveSettings(): void {
    const f = this.settingsForm;
    if (!f) return;
    this.savingSettings = true;
    this.purchasing
      .updateSettings({
        check_days: f.days.join(','),
        remind_times: f.remindTimes,
        responsible_user_id: f.responsible,
        escalate_user_id: f.approver,
        auto_order_limit_huf: Number(f.limit) || 0,
        delivery_note: f.deliveryNote,
      })
      .subscribe({
        next: (s) => {
          this.settings = s;
          const done = () => {
            this.savingSettings = false;
            this.settingsOpen = false;
            this.notification.show('Beszerzési beállítások mentve.', 'success');
            this.load();
          };
          if (!this.profileForm) return done();
          this.purchasing.updateProfile(this.profileForm).subscribe({
            next: (p) => {
              this.profile = p;
              done();
            },
            error: (e) => {
              this.savingSettings = false;
              this.notification.show(e?.error?.message || 'A rendelési adatok mentése nem sikerült.', 'danger');
            },
          });
        },
        error: (e) => {
          this.savingSettings = false;
          this.notification.show(e?.error?.message || 'Nem sikerült menteni.', 'danger');
        },
      });
  }

  trackLine(_: number, line: PurchaseListLine): number {
    return line.itemId;
  }
}
